/**
 * useKeyboardPinnedFooter — keeps a fixed bottom bar above the software keyboard.
 *
 * WHY THIS EXISTS
 * iOS Safari does NOT shrink the layout viewport when the keyboard opens. `100vh`,
 * `100dvh`, `100svh` and `window.innerHeight` all keep their bars-visible values,
 * and `position: fixed; bottom: 0` is resolved against that same layout viewport —
 * so a footer button sits BEHIND the keyboard and the user has to tap Done first
 * (reported on the join flow's number, code and name screens, 2026-09-03).
 * The only signal that the keyboard exists is `window.visualViewport`, whose
 * height shrinks by the keyboard + accessory bar and whose `offsetTop` grows when
 * Safari pans to reveal the focused input. Hence a JS translate.
 *
 * Android Chrome defaults to `interactive-widget: resizes-content`, so the layout
 * viewport really does shrink there and the footer already clears the keyboard:
 * the offset computes to 0 and this hook is a no-op. Do NOT add
 * `interactive-widget=resizes-visual` to the viewport meta — that would make
 * Chrome behave like Safari and put Android on this code path too.
 *
 * Returns { footerRef, footerHeight, pinned }:
 *  - footerRef      attach to the fixed footer element
 *  - footerHeight   reserve this as bottom padding on the scrolling column, so
 *                   content is never hidden under the out-of-flow footer
 *  - pinned         true while riding above an open keyboard (drop the
 *                   safe-area inset then, or the home-indicator gap is counted
 *                   twice above the keyboard)
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Below this, the delta is browser-toolbar jitter or a pinch-zoom, not a keyboard.
const KEYBOARD_MIN_PX = 80;
// Breathing room between the focused field and the top of the footer.
const FIELD_CLEARANCE_PX = 8;

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
}

export default function useKeyboardPinnedFooter() {
  const footerRef = useRef(null);
  const frameRef = useRef(0);
  const scheduledRef = useRef(false);
  const [footerHeight, setFooterHeight] = useState(0);
  const [pinned, setPinned] = useState(false);

  const measure = useCallback(() => {
    const el = footerRef.current;
    if (el) setFooterHeight(el.offsetHeight);
  }, []);

  // Reserve the footer's space before paint, and keep it correct if the button
  // reflows (an error message appearing, a font swap, an orientation change).
  useLayoutEffect(() => {
    measure();
    const el = footerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    // No visualViewport (jsdom in tests, older Safari, some Android WebViews):
    // leave the footer exactly where CSS put it. Never worse than today.
    if (!vv) return undefined;

    const apply = () => {
      scheduledRef.current = false;
      frameRef.current = 0;
      const el = footerRef.current;
      if (!el) return;

      const active = document.activeElement;
      const editing = isEditable(active);
      // How much of the layout viewport the keyboard covers, accounting for
      // Safari panning the visual viewport up to reveal the focused field.
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      const shouldPin = editing && covered > KEYBOARD_MIN_PX;

      el.style.transform = shouldPin ? `translate3d(0, ${-covered}px, 0)` : '';
      setPinned(shouldPin);

      // The footer must never cover the field being typed into (short screens,
      // long content). scrollBy, not scrollIntoView, which jumps on iOS.
      if (shouldPin && active) {
        const rect = active.getBoundingClientRect();
        const footerTop = vv.offsetTop + vv.height - el.offsetHeight;
        if (rect.bottom > footerTop) {
          window.scrollBy(0, rect.bottom - footerTop + FIELD_CLEARANCE_PX);
        }
      }
    };

    // iOS fires a single resize once the keyboard animation finishes, so
    // coalescing costs nothing and protects against scroll-event storms.
    const schedule = () => {
      // The "already queued" flag is set BEFORE requesting the frame and cleared
      // at the top of apply, so the guard tracks whether work is pending rather
      // than whether an id happens to be assigned. Assigning the id first would
      // latch the guard on forever if the callback ever ran synchronously.
      if (scheduledRef.current) return;
      scheduledRef.current = true;
      frameRef.current = window.requestAnimationFrame(apply);
    };

    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    schedule();

    // Captured now, not read in the cleanup: by teardown the ref may already
    // point at a different node (or null).
    const footerAtMount = footerRef.current;
    return () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
      scheduledRef.current = false;
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('scroll', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      if (footerAtMount) footerAtMount.style.transform = '';
    };
  }, []);

  return { footerRef, footerHeight, pinned };
}
